import React, { useState, useEffect } from 'react';
import { Contract } from 'ethers';
import type { BrowserProvider } from 'ethers';
import styles from './TokenInput.module.css';
import { ERC20_ABI } from '../constants/abis';
import { formatTokenAmount } from '../utils/formatting';

export interface TokenMeta {
  address: string;
  symbol: string;
  decimals: number;
  name?: string;
  logoURI?: string;
}

interface TokenInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  token: TokenMeta | null;
  onTokenChange: (token: TokenMeta) => void;
  balance?: bigint;
  disabled?: boolean;
  readOnly?: boolean;
  provider: BrowserProvider | null;
  userAddress: string | null;
  showMax?: boolean;
  suggestedTokens?: TokenMeta[];
}

export function TokenInput({
  label,
  value,
  onChange,
  token,
  onTokenChange,
  balance,
  disabled,
  readOnly,
  provider,
  userAddress,
  showMax,
  suggestedTokens = [],
}: TokenInputProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [searchAddr, setSearchAddr] = useState('');
  const [lookupMeta, setLookupMeta] = useState<TokenMeta | null>(null);
  const [lookupError, setLookupError] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);

  const formattedBalance =
    balance !== undefined && token
      ? formatTokenAmount(balance, token.decimals)
      : null;

  const handleMax = () => {
    if (balance !== undefined && token && !disabled) {
      const raw = balance.toString();
      // Format full precision
      const full = (Number(balance) / 10 ** token.decimals).toFixed(token.decimals);
      onChange(full.replace(/\.?0+$/, '') || '0');
    }
  };

  // Lookup token by address
  useEffect(() => {
    if (!searchAddr || !provider || searchAddr.length < 42) {
      setLookupMeta(null);
      setLookupError('');
      return;
    }
    setLookupLoading(true);
    setLookupError('');
    const contract = new Contract(searchAddr, ERC20_ABI, provider);
    Promise.all([
      contract.symbol() as Promise<string>,
      contract.decimals() as Promise<bigint>,
      contract.name() as Promise<string>,
    ])
      .then(([symbol, decimals, name]) => {
        setLookupMeta({ address: searchAddr, symbol, decimals: Number(decimals), name });
        setLookupLoading(false);
      })
      .catch(() => {
        setLookupError('Not a valid ERC-20 token on this network.');
        setLookupMeta(null);
        setLookupLoading(false);
      });
  }, [searchAddr, provider]);

  const selectToken = (t: TokenMeta) => {
    onTokenChange(t);
    setPickerOpen(false);
    setSearchAddr('');
    setLookupMeta(null);
  };

  return (
    <div className={`${styles.wrap} ${disabled ? styles.disabled : ''}`}>
      <div className={styles.labelRow}>
        <span className={styles.label}>{label}</span>
        {formattedBalance !== null && (
          <span className={styles.balance}>
            Balance: {formattedBalance}
            {showMax && !readOnly && (
              <button className={styles.maxBtn} onClick={handleMax} type="button">MAX</button>
            )}
          </span>
        )}
      </div>

      <div className={styles.inputRow}>
        <input
          className={styles.input}
          type="text"
          inputMode="decimal"
          placeholder="0.0"
          value={value}
          onChange={e => {
            // Allow only valid decimal input
            const v = e.target.value;
            if (/^[0-9]*\.?[0-9]*$/.test(v) || v === '') onChange(v);
          }}
          disabled={disabled || readOnly}
          readOnly={readOnly}
          autoComplete="off"
        />
        <button
          className={styles.tokenBtn}
          type="button"
          onClick={() => setPickerOpen(true)}
        >
          {token ? (
            <>
              {token.logoURI ? (
                <img src={token.logoURI} alt='' className={styles.tokenLogo} />
              ) : null}
              <span className={styles.tokenSymbol}>{token.symbol}</span>
              <ChevronIcon />
            </>
          ) : (
            <>
              <span className={styles.selectText}>Select token</span>
              <ChevronIcon />
            </>
          )}
        </button>
      </div>

      {/* Token Picker Modal */}
      {pickerOpen && (
        <div className={styles.pickerOverlay} onClick={e => { if (e.target === e.currentTarget) setPickerOpen(false); }}>
          <div className={styles.picker}>
            <div className={styles.pickerHeader}>
              <h4>Select Token</h4>
              <button className={styles.pickerClose} onClick={() => setPickerOpen(false)}>×</button>
            </div>

            <input
              className={styles.pickerSearch}
              type="text"
              placeholder="Paste token address (0x…)"
              value={searchAddr}
              onChange={e => setSearchAddr(e.target.value.trim())}
              autoFocus
            />

            {lookupLoading && <p className={styles.lookupMsg}>Resolving token…</p>}
            {lookupError && <p className={styles.lookupError}>{lookupError}</p>}
            {lookupMeta && (
              <div className={styles.lookupResult} onClick={() => selectToken(lookupMeta)}>
                <span className={styles.tokenSymbol}>{lookupMeta.symbol}</span>
                <span className={styles.tokenName}>{lookupMeta.name}</span>
                <span className={styles.tokenAddr}>{lookupMeta.address.slice(0, 10)}…</span>
              </div>
            )}

            {suggestedTokens.length > 0 && (
              <>
                <p className={styles.sectionLabel}>Common tokens</p>
                <div className={styles.tokenList}>
                  {suggestedTokens.map(t => (
                    <button
                      key={t.address}
                      className={`${styles.tokenListItem} ${token?.address === t.address ? styles.selected : ''}`}
                      onClick={() => selectToken(t)}
                    >
                      {t.logoURI ? (
                        <img src={t.logoURI} alt='' className={styles.tokenLogo} />
                      ) : null}
                      <span className={styles.tokenItemSymbol}>{t.symbol}</span>
                      <span className={styles.tokenItemAddr}>{t.address.slice(0, 8)}…{t.address.slice(-4)}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ChevronIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
